import { VesselTable } from "../../../certificates/components/VesselTable";
import { docTypes } from "../../../certificates/const/doc-types.const";
import { Certificate, DropdownVessel } from "../../../certificates/models/certificates.interfaces";
import { DocumentIcon, ShipIcon } from "../../../shared/Icons";
import { documentsAtom, themeAtom } from "../../../shared/atoms";
import Datepicker from "../../../shared/components/Datepicker/Datepicker";
import Dropdown from "../../../shared/components/Dropdown";
import { FileSuccessIcon } from "../../../shared/components/FileUploader/FileStatuses";
import { log } from "../../../shared/services/debug-logger";
import { cn, dateToUtc } from "../../../shared/services/utils";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  document: Certificate;
  vesselOptions: DropdownVessel[];
  showVesselSyncButton: boolean;
  showErrors: boolean;
  onVesselSelected: (fileName: string, vessel: DropdownVessel) => void;
}

type ValidationError = Partial<Record<"vesselName" | "documentTypes" | "expirationDate" | "surveyDate", string>>;

const DocumentInformation = ({
  document,
  vesselOptions,
  showVesselSyncButton,
  showErrors,
  onVesselSelected,
}: Props) => {
  const theme = useAtomValue(themeAtom);

  const [documents, setDocuments] = useAtom(documentsAtom);
  const [errors, setErrors] = useState<ValidationError>({});
  const [docTypeChanged, setDocTypeChanged] = useState<boolean>(false);
  const [expDateChanged, setExpDateChanged] = useState<boolean>(false);
  const [surveyDateChanged, setSurveyDateChanged] = useState<boolean>(false);
  const [filteredVessels, setFilteredVessels] = useState<DropdownVessel[]>(vesselOptions);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasVesselSyncEnabled, setHasVesselSyncEnabled] = useState<boolean>(false);

  const chosenDocumentTypesForVessel = documents
    .filter((doc) => doc.vesselCode === document.vesselCode && doc.fileName !== document.fileName)
    .map((doc) => doc.docName);

  const availableDocumentTypes = docTypes
    .map((dt) => dt.name)
    .filter((docType) => !chosenDocumentTypesForVessel.includes(docType) || docType === document.docName);

  const updateDocument = (fileName: string | undefined, updates: Partial<Certificate>) => {
    setDocuments((prevDocs) => prevDocs.map((doc) => (doc.fileName === fileName ? { ...doc, ...updates } : doc)));
  };
  const showExpirationError = showErrors && errors.expirationDate && !document.expiryNotApplicable;
  const showSurveyDateError = showErrors && errors.surveyDate;
  const handleSelectVessel = (selectedVessel: DropdownVessel) => {
    log("DocumentInformation - handleSelectVessel", { vesselCode: selectedVessel.code, batch: hasVesselSyncEnabled });
    setIsDropdownOpen(false);
    if (hasVesselSyncEnabled) {
      documents.forEach((doc) => onVesselSelected(doc.fileName ?? "", selectedVessel));
      setDocuments((prevDocs) =>
        prevDocs.map((doc) => ({
          ...doc,
          vesselName: selectedVessel.vesselName,
          imoNumber: selectedVessel.imoNumber,
          vesselCode: selectedVessel.code,
        })),
      );
    } else {
      onVesselSelected(document.fileName ?? "", selectedVessel);
      updateDocument(document.fileName, {
        vesselName: selectedVessel.vesselName,
        imoNumber: selectedVessel.imoNumber,
        vesselCode: selectedVessel.code,
      });
    }
    const changedCount = hasVesselSyncEnabled
      ? documents.length
      : documents.filter((doc) => doc.fileName === document.fileName).length;
    log("STATE - handleSelectVessel - applied", { count: changedCount });
  };

  const handleSyncChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    log("DocumentInformation - handleSyncChange", { enabled: checked });
    setHasVesselSyncEnabled(checked);
    if (checked) {
      const selectedVessel = {
        vesselName: document.vesselName,
        imoNumber: document.imoNumber,
        code: document.vesselCode,
        type: "",
      };
      documents.forEach((doc) => onVesselSelected(doc.fileName ?? "", selectedVessel));
      setDocuments((prevDocs) =>
        prevDocs.map((doc) => ({
          ...doc,
          vesselName: document.vesselName,
          imoNumber: document.imoNumber,
          vesselCode: document.vesselCode,
        })),
      );
      log("STATE - handleSyncChange - applied", { count: documents.length, vesselCode: document.vesselCode });
    }
  };

  const setExpiryNotApplicable = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    updateDocument(document.fileName, {
      expiryNotApplicable: checked,
    });
  };

  const setPortOfIssueIsWhoApproved = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    updateDocument(document.fileName, {
      portOfIssueIsWhoApproved: checked,
    });
  };

  // Note that for expiry dates we don't want them adjusted to UTC.
  // We want them as calendaristic dates without UTC adjustment.
  const handleSelectExpiration = (expiresOn: Date) => {
    updateDocument(document.fileName, {
      expiresOn: expiresOn,
    });
    setExpDateChanged(true);
  };

  const handleSelectDocument = (selectedDocName: string) => {
    const docType = docTypes.find((dt) => dt.name === selectedDocName);
    updateDocument(document.fileName, {
      docName: selectedDocName,
      docType: docType?.docType || "Certificates",
      expiryNotApplicable: !docType?.hasExpirationDate,
      expiresOn: docType?.hasExpirationDate ? document.expiresOn : null,
      hasSurveyDate: docType?.hasSurveyDate || false,
    });
    setDocTypeChanged(true);
  };

  // Note that for expiry dates we don't want them adjusted to UTC.
  // We want them as calendaristic dates without UTC adjustment.
  const handleNextSurveyDate = (nextSurveyDate: Date) => {
    updateDocument(document.fileName, {
      nextSurveyDate: dateToUtc(nextSurveyDate),
    });
    setSurveyDateChanged(true);
  };

  useEffect(() => {
    setFilteredVessels(vesselOptions);
  }, [vesselOptions]);

  useEffect(() => {
    if (!!document.expiresOn && !expDateChanged) handleSelectExpiration(document.expiresOn);
    if (!!document.nextSurveyDate && !surveyDateChanged) handleNextSurveyDate(document.nextSurveyDate);
    if (!!document.docName && !docTypeChanged) handleSelectDocument(document.docName);
  });

  useEffect(() => {
    if (!showErrors) {
      setErrors({});
      return;
    }

    const newErrors: ValidationError = {};
    if (!document.vesselName) {
      newErrors.vesselName = "Empty field please select an option.";
    }
    if (!document.docName) {
      newErrors.documentTypes = "Empty field please select an option.";
    }
    if (!document.expiresOn) {
      newErrors.expirationDate = "Field is empty.";
    }
    if (!document.nextSurveyDate) {
      newErrors.surveyDate = "Field is empty.";
    }
    setErrors(newErrors);
  }, [showErrors, document]);

  return (
    <div
      className={cn(
        "flex items-center gap-[24px] py-[24px]",
        { "pb-[48px]": showVesselSyncButton },
        twMerge(theme.home.tailwind.textDefault),
      )}
    >
      <div className="flex w-[25%] items-center gap-4 truncate text-ellipsis text-nowrap pt-[20px]">
        <FileSuccessIcon className="flex-shrink-0" />
        {document.cachedFile?.name}
      </div>

      <div className="relative">
        <div
          className={twMerge(
            "h-[25px] text-sm font-medium",
            theme.balticQuestionnaire.inputsBg,
            theme.home.tailwind.textDefault,
          )}
        >
          Vessel
        </div>
        <Dropdown
          icon={<ShipIcon />}
          placeholder={"Select a vessel"}
          value={document.vesselName}
          floatingComponent={
            <VesselTable
              isOpen={isDropdownOpen}
              vessels={filteredVessels}
              onSelectVessel={(vessel) => {
                handleSelectVessel(vessel);
              }}
            />
          }
          disabled={vesselOptions.length === 0}
          isLoading={vesselOptions.length === 0}
          onClick={() => setIsDropdownOpen(true)}
          onChange={(event) => {
            // @ts-expect-error event could be 2 types, value only exists on one of them, it works but should be fixed
            const userInput = event.currentTarget.value.toLowerCase();
            const filtered = vesselOptions.filter((v) => v.vesselName.toLowerCase().includes(userInput));
            setFilteredVessels(filtered);
          }}
          error={showErrors ? errors.vesselName : undefined}
        />

        {showVesselSyncButton && (
          <div className="absolute top-[100%] mt-2 flex items-center">
            <input type="checkbox" checked={hasVesselSyncEnabled} onChange={handleSyncChange} id="sync" />
            <label htmlFor="sync" className={twMerge("text-sm", theme.uwhcAssistant.text)}>
              Same for the other documents
            </label>
          </div>
        )}
      </div>

      <div className="relative">
        <div className="h-[25px] text-sm font-medium">Document Type</div>
        <Dropdown
          icon={<DocumentIcon width="100%" height="100%" />}
          placeholder={"Document name"}
          onClick={() => setIsDropdownOpen(true)}
          value={document.docName}
          onSelectOption={(selectedDocName) => handleSelectDocument(selectedDocName)}
          options={availableDocumentTypes}
          error={showErrors ? errors.documentTypes : undefined}
        />
        {document.docName === "Ship Sanitation Control (SSCC)/Ship Sanitation Control Exemption (SSCE) Certificate" && (
          <div className="absolute top-[100%] mt-2 flex items-center">
            <input
              type="checkbox"
              checked={document.portOfIssueIsWhoApproved}
              onChange={setPortOfIssueIsWhoApproved}
              id="who"
            />
            <label htmlFor="who" className="text-sm">
              Port of issue is WHO approved
            </label>
          </div>
        )}
      </div>

      <div className="relative">
        <div className="h-[25px] text-sm font-medium">Expiration Date</div>
        <Datepicker
          onChange={handleSelectExpiration}
          placeholderText="Expiration date"
          selected={document.expiresOn || null}
          isDisabled={document.expiryNotApplicable}
          error={showExpirationError ? errors.expirationDate : undefined}
        />
        {showExpirationError && (
          <div className="absolute top-[100%] mt-2 flex items-center">
            <p className="text-sm text-[#bf0002]">{errors.expirationDate}</p>
          </div>
        )}

        {document.docName !== "Baltic questionnaire" && (
          <div className={cn("absolute top-[100%] mt-2 flex items-center", { "mt-8": showExpirationError })}>
            <input
              type="checkbox"
              className={theme.home.tailwind.panelBackground}
              checked={document.expiryNotApplicable}
              onChange={setExpiryNotApplicable}
              id="sync"
            />
            <label htmlFor="sync" className={twMerge("text-sm", theme.uwhcAssistant.text)}>
              Not applicable
            </label>
          </div>
        )}
      </div>

      {document.hasSurveyDate && document.docName && (
        <div className="relative">
          <div className="h-[25px] text-sm font-medium">Next Survey Date</div>
          <Datepicker
            onChange={handleNextSurveyDate}
            placeholderText="Next Survey Date"
            selected={document.nextSurveyDate || null}
            error={showSurveyDateError ? errors.surveyDate : undefined}
          />
          {showSurveyDateError && (
            <div className="absolute top-[100%] mt-2 flex items-center">
              <p className="text-sm text-[#bf0002]">{errors.surveyDate}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentInformation;
